// Global
import React from 'react';

import { Field, Item } from '@sitecore-content-sdk/nextjs';

// Local
import { Newsletter } from '../../../../.generated/Newsletter/NewsletterSignup.model';
import { NewsletterSignup } from './NewsletterSignup';
import PlainTextWrapper from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import ImageWrapper from 'components/helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { Themes } from 'lib/themes';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { Subscriptions } from 'lib/services/newsletter-signup-service';
import { useSitecorePage } from 'lib/hooks/sitecore/useSitecorePage';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import { Data } from '@generated/Foundation.GMI.model';
import { getQuestionOptions } from 'lib/utils/question-options';

type NewsletterSignupProps = Newsletter.NewsletterSignup.NewsletterSignup_Component;
type NewsletterSignupFieldsWithBusinessIndustries = NonNullable<NewsletterSignupProps['fields']> & {
  isRequired?: Field<boolean>;
  questionId?: Field<string>;
  questionLabel?: Field<string>;
  questionPlaceholder?: Field<string>;
  questionsDropdownList?: Item[];
};

export const Default = (props: NewsletterSignupProps) => {
  const { getDictionaryValue } = useDictionary();
  const siteSettings = useSiteSettings();
  const route = useSitecorePage();
  const { siteName } = useGlobalData();
  const hideNewsletterSignup = route?.fields?.hideNewsletterSignupBanner?.value;
  const fields = props.fields as NewsletterSignupFieldsWithBusinessIndustries | undefined;
  const isNafSite = siteName === 'GMFS';
  const loginRegistrationSettings =
    siteSettings?.loginRegistrationSettings as Data.Settings.LoginAndRegistrationSettings;
  const enableQuestionsDropdown =
    loginRegistrationSettings?.fields?.enableQuestionsDropdown?.value || false;

  const businessIndustryOptions = getQuestionOptions(fields?.questionsDropdownList || []);
  const showBusinessIndustryDropdown =
    (isNafSite || enableQuestionsDropdown) &&
    businessIndustryOptions.length > 0 &&
    !!fields?.questionId?.value;
  // Required is driven by the shared _BaseQuestions "isRequired" field, only when the
  // dropdown is actually shown (mirrors the SignUp component's visibility vs. required split).
  const isBusinessIndustryRequired = showBusinessIndustryDropdown && !!fields?.isRequired?.value;

  const emailErrorMessage =
    getDictionaryValue('Components.NewsletterSignup.EmailValidation') ||
    'Please enter a valid email address.';
  const businessIndustryErrorMessage =
    getDictionaryValue('Components.NewsletterSignup.BusinessIndustryError') ||
    'Please select a business industry';

  const subscriptionId = siteSettings?.subscriptionID?.value;
  const hasImage = !!(
    props.fields?.image && (props.fields.image as { value?: { src?: string } }).value?.src
  );

  const subscriptions: Subscriptions[] | undefined = subscriptionId
    ? [
        {
          SubscriptionId: subscriptionId,
          Subscribed: true,
        },
      ]
    : undefined;

  if (!props.fields || hideNewsletterSignup) {
    return <></>;
  }

  return (
    <NewsletterSignup
      theme={props.params?.theme as Themes}
      image={
        hasImage ? (
          <ImageWrapper field={props.fields?.image} fallbackWidth={1600} fallbackHeight={1067} />
        ) : undefined
      }
      hasImage={hasImage}
      headline={<PlainTextWrapper field={props.fields?.headline} />}
      description={<RichTextWrapper field={props.fields?.description} />}
      emailLabel={<PlainTextWrapper field={props.fields?.emailLabel} />}
      emailPlaceholder={props.fields?.emailPlaceholderText?.value || 'Enter your email'}
      businessIndustryLabel={fields?.questionLabel?.value || 'Business Industry'}
      businessIndustryPlaceholder={
        fields?.questionPlaceholder?.value || 'Select a business industry'
      }
      businessIndustryOptions={businessIndustryOptions}
      showBusinessIndustryDropdown={showBusinessIndustryDropdown}
      businessIndustryRequired={isBusinessIndustryRequired}
      businessIndustryQuestionId={
        showBusinessIndustryDropdown ? fields?.questionId?.value : undefined
      }
      submitButton={
        <CtaButton type="submit" ctaStyle="filled" ctaVariant="brand">
          <PlainTextWrapper
            field={props.fields?.signupButtonText}
            tag="span"
            defaultValue={
              getDictionaryValue('Components.NewsletterSignup.SubmitButton') || 'Subscribe'
            }
          />
        </CtaButton>
      }
      legalText={<RichTextWrapper field={props.fields?.legalText} />}
      loginLink={(email: string) => {
        const linkField = props.fields?.loginLink;
        if (!linkField) return undefined;

        // Handle both LinkField and LinkFieldValue structures
        const fieldValue = linkField.value || linkField;
        const existingQuerystring = fieldValue?.querystring || '';

        // Append or set the email query parameter
        const newQuerystring = existingQuerystring
          ? `${existingQuerystring}&email=${encodeURIComponent(email)}`
          : `email=${encodeURIComponent(email)}`;

        // Fix Sitecore anchor linktype that prefixes href with '#'
        const href = (fieldValue?.href || '').replace(/^#(https?:\/\/)/, '$1');

        // Create updated field with email query parameter and fixed href
        return {
          value: {
            ...(linkField.value || linkField),
            href,
            querystring: newQuerystring,
          },
        };
      }}
      successHeadline={<PlainTextWrapper field={props.fields?.successHeadline} />}
      successDescription={<RichTextWrapper field={props.fields?.successDescription} />}
      emailErrorMessage={emailErrorMessage}
      businessIndustryErrorMessage={businessIndustryErrorMessage}
      subscriptions={subscriptions}
      // Analytics props
      newsletterTitle={siteSettings?.newsletterTitle?.value || `${siteName}'s Email`}
      subscriberPosition="Footer"
      successHeadlineForLoggedIn={props.fields?.successHeadlineForLoggedIn?.value || 'Subscribed!'}
    />
  );
};
